import { IconButton, SxProps, Theme, Tooltip, useTheme } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Megaphone } from "src/icons/megaphone";

type Props = {
  sx?: SxProps<Theme>;
};

const SentryReportBugButton = ({ sx }: Props) => {
  const { t } = useTranslation();
  const {
    palette: { mode },
  } = useTheme();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const feedback = Sentry.getFeedback();
    const button = buttonRef.current;

    if (!feedback || !button) return;

    feedback.remove();

    const detachFeedback = feedback.attachTo(button, {
      colorScheme: mode,
      triggerLabel: t("common:sentryFeedback.triggerLabel"),
      triggerAriaLabel: t("common:sentryFeedback.triggerAriaLabel"),
      cancelButtonLabel: t("common:sentryFeedback.cancelButtonLabel"),
      submitButtonLabel: t("common:sentryFeedback.submitButtonLabel"),
      confirmButtonLabel: t("common:sentryFeedback.confirmButtonLabel"),
      formTitle: t("common:sentryFeedback.formTitle"),
      emailLabel: t("common:sentryFeedback.emailLabel"),
      emailPlaceholder: t("common:sentryFeedback.emailPlaceholder"),
      messageLabel: t("common:sentryFeedback.messageLabel"),
      messagePlaceholder: t("common:sentryFeedback.messagePlaceholder"),
      nameLabel: t("common:sentryFeedback.nameLabel"),
      namePlaceholder: t("common:sentryFeedback.namePlaceholder"),
      successMessageText: t("common:sentryFeedback.successMessageText"),
      isRequiredLabel: t("common:sentryFeedback.isRequiredLabel"),
      addScreenshotButtonLabel: t("common:sentryFeedback.addScreenshotButtonLabel"),
      removeScreenshotButtonLabel: t("common:sentryFeedback.removeScreenshotButtonLabel"),
      highlightToolText: t("common:sentryFeedback.highlightToolText"),
      hideToolText: t("common:sentryFeedback.hideToolText"),
      removeHighlightText: t("common:sentryFeedback.removeHighlightText"),
    });

    return () => {
      detachFeedback();
      feedback.remove();
    };
  }, [mode, t]);

  return (
    <Tooltip title={t("common:reportBug")}>
      <IconButton
        id="sentry-feedback-button"
        ref={buttonRef}
        color="inherit"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          ...sx,
        }}>
        <Megaphone color="primary" sx={{ width: 20, height: 20 }} />
      </IconButton>
    </Tooltip>
  );
};

export default SentryReportBugButton;
